import { Play } from 'lucide-react';
import { useState } from 'react';

type DeferredVideoProps = {
  src: string;
  poster: string;
  posterSrcSet?: string;
  posterSizes?: string;
  alt: string;
  className?: string;
  width: number;
  height: number;
  onPlay?: () => void;
  captionsSrc?: string;
  showPlayButton?: boolean;
  priority?: boolean;
};

/**
 * Keeps high-transfer video out of the initial network waterfall. The video is
 * only added to the page when a visitor deliberately starts it.
 */
export default function DeferredVideo({
  src,
  poster,
  posterSrcSet,
  posterSizes,
  alt,
  className = '',
  width,
  height,
  onPlay,
  captionsSrc,
  showPlayButton = true,
  priority = false,
}: DeferredVideoProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  if (isPlaying) {
    return (
      <video
        src={src}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className={className}
        width={width}
        height={height}
        aria-label={alt}
        onPlay={onPlay}
      >
        {captionsSrc && <track kind="captions" src={captionsSrc} srcLang="en" label="English" default />}
      </video>
    );
  }

  return (
    <button
      type="button"
      className="group relative block h-full w-full overflow-hidden text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      onClick={() => setIsPlaying(true)}
      aria-label={`Play ${alt}`}
    >
      <img
        src={poster}
        srcSet={posterSrcSet}
        sizes={posterSizes}
        alt={alt}
        className={`${className} transition-transform duration-300 group-hover:scale-[1.02]`}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        fetchPriority="auto"
      />
      {showPlayButton && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/30">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
            <Play className="ml-0.5 h-5 w-5 fill-current" aria-hidden="true" />
          </span>
        </span>
      )}
    </button>
  );
}
